import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
  LineChart, Line, Area, AreaChart,
} from 'recharts';
import {
  TrendingDown, TrendingUp, ShoppingCart, Building2, Calendar, RefreshCw, Loader2,
} from 'lucide-react';
import { api } from '../../../utils/api';

// ── Types ─────────────────────────────────────────────────────────────────────

interface ExpenseReport {
  period: { from: string; to: string };
  totals: {
    total_expenses: number;
    subtotal_expenses: number;
    total_tax: number;
    order_count: number;
  };
  by_category: { category: string; total: number; count: number }[];
  by_month:    { month: string; total: number; count: number }[];
  top_suppliers: { supplier_name: string; total: number; count: number }[];
  comparison: { month: string; expenses: number; income: number; profit: number }[];
}

// ── Constants ─────────────────────────────────────────────────────────────────

const CATEGORY_LABELS: Record<string, string> = {
  INSUMOS:            '🥩 Insumos',
  SERVICIOS:          '🔧 Servicios',
  NOMINA:             '👥 Nómina',
  MANTENIMIENTO:      '⚙️ Mantenimiento',
  ARRIENDO:           '🏠 Arriendo',
  SERVICIOS_PUBLICOS: '💡 Servicios públicos',
  OTROS:              '📦 Otros',
};

const CATEGORY_COLORS = [
  '#6366f1', '#f59e0b', '#10b981', '#ef4444',
  '#3b82f6', '#8b5cf6', '#ec4899',
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(n: number) {
  return '$ ' + Math.round(n).toLocaleString('es-CO');
}

function fmtShort(n: number) {
  if (n >= 1_000_000) return '$ ' + (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000)     return '$ ' + (n / 1_000).toFixed(0) + 'k';
  return '$ ' + Math.round(n);
}

function monthLabel(ym: string) {
  const [y, m] = ym.split('-');
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString('es-CO', { month: 'short', year: '2-digit' });
}

// Presets
const today = new Date().toISOString().slice(0, 10);
const startOfYear = new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10);
const last30 = new Date(Date.now() - 30 * 86_400_000).toISOString().slice(0, 10);
const last90 = new Date(Date.now() - 90 * 86_400_000).toISOString().slice(0, 10);

const PRESETS = [
  { label: 'Este año',     from: startOfYear, to: today },
  { label: 'Últimos 90 d', from: last90,      to: today },
  { label: 'Últimos 30 d', from: last30,      to: today },
];

// ── Custom Tooltip ────────────────────────────────────────────────────────────

function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-border bg-surface px-4 py-3 text-sm shadow-lg">
      <p className="mb-1 font-semibold text-text">{label}</p>
      {payload.map((p: any) => (
        <p key={p.dataKey} style={{ color: p.color ?? p.fill }}>
          {p.name}: {fmtShort(p.value)}
        </p>
      ))}
    </div>
  );
}

// ── KPI Card ──────────────────────────────────────────────────────────────────

function KpiCard({ label, value, sub, icon, accent = 'bg-primary/10 text-primary', loading }: {
  label: string; value: string; sub?: string; icon: React.ReactNode;
  accent?: string; loading?: boolean;
}) {
  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-border bg-surface p-5 shadow-card">
      <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${accent}`}>
        {icon}
      </div>
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-text-muted">{label}</p>
        {loading
          ? <div className="mt-1 h-7 w-28 animate-pulse rounded bg-muted" />
          : <p className="mt-0.5 text-2xl font-bold text-text">{value}</p>}
        {sub && !loading && <p className="mt-0.5 text-xs text-text-muted">{sub}</p>}
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function ExpenseReportPage() {
  const [from, setFrom] = useState(startOfYear);
  const [to,   setTo]   = useState(today);

  const { data, isLoading, isFetching, refetch } = useQuery<ExpenseReport>({
    queryKey: ['expense-report', from, to],
    queryFn: async () => {
      const { data } = await api.get('/purchases/expense-report', { params: { from, to } });
      return data;
    },
    staleTime: 60_000,
  });

  const totals      = data?.totals;
  const byCategory  = data?.by_category  ?? [];
  const byMonth     = (data?.by_month    ?? []).map(r => ({ ...r, label: monthLabel(r.month) }));
  const comparison  = (data?.comparison  ?? []).map(r => ({ ...r, label: monthLabel(r.month) }));
  const topSuppliers = data?.top_suppliers ?? [];

  const avgMonthly = byMonth.length > 0
    ? (totals?.total_expenses ?? 0) / byMonth.length
    : 0;

  const topCat = byCategory[0];

  return (
    <div className="min-h-screen bg-bg p-4 md:p-6">
      <div className="mx-auto max-w-7xl space-y-6">

        {/* ── Header ── */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-text">Reporte de Gastos</h1>
            <p className="text-sm text-text-muted">Análisis de compras e insumos</p>
          </div>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white shadow transition hover:opacity-90 disabled:opacity-60"
          >
            {isFetching
              ? <Loader2 size={14} className="animate-spin" />
              : <RefreshCw size={14} />}
            Actualizar
          </button>
        </div>

        {/* ── Date filter ── */}
        <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-border bg-surface p-4 shadow-card">
          <div className="flex flex-wrap gap-2">
            {PRESETS.map(p => (
              <button
                key={p.label}
                onClick={() => { setFrom(p.from); setTo(p.to); }}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                  from === p.from && to === p.to
                    ? 'bg-primary text-white'
                    : 'bg-muted/40 text-text-muted hover:bg-muted/70'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 ml-auto flex-wrap">
            <Calendar size={14} className="text-text-muted shrink-0" />
            <input
              type="date" value={from} onChange={e => setFrom(e.target.value)}
              className="rounded-lg border border-border bg-bg px-2 py-1.5 text-xs text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
            <span className="text-xs text-text-muted">→</span>
            <input
              type="date" value={to} onChange={e => setTo(e.target.value)}
              className="rounded-lg border border-border bg-bg px-2 py-1.5 text-xs text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>
        </div>

        {/* ── KPIs ── */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <KpiCard
            label="Total gastos"
            value={fmt(totals?.total_expenses ?? 0)}
            sub={`${totals?.order_count ?? 0} facturas`}
            icon={<TrendingDown size={20} />}
            accent="bg-red-100 text-red-600"
            loading={isLoading}
          />
          <KpiCard
            label="Promedio mensual"
            value={fmt(avgMonthly)}
            sub={`${byMonth.length} mes${byMonth.length !== 1 ? 'es' : ''}`}
            icon={<Calendar size={20} />}
            accent="bg-amber-100 text-amber-600"
            loading={isLoading}
          />
          <KpiCard
            label="Categoría mayor"
            value={topCat ? fmt(topCat.total) : '—'}
            sub={topCat ? CATEGORY_LABELS[topCat.category] ?? topCat.category : ''}
            icon={<ShoppingCart size={20} />}
            accent="bg-violet-100 text-violet-600"
            loading={isLoading}
          />
          <KpiCard
            label="IVA pagado"
            value={fmt(totals?.total_tax ?? 0)}
            sub="Total impuestos en compras"
            icon={<Building2 size={20} />}
            accent="bg-blue-100 text-blue-600"
            loading={isLoading}
          />
        </div>

        {/* ── Charts row 1: Monthly + Pie ── */}
        <div className="grid gap-6 lg:grid-cols-5">

          {/* Monthly bar chart */}
          <div className="lg:col-span-3 rounded-2xl border border-border bg-surface p-5 shadow-card">
            <h2 className="mb-4 text-base font-semibold text-text">Gastos por mes</h2>
            {isLoading ? (
              <div className="flex h-52 items-center justify-center">
                <Loader2 size={28} className="animate-spin text-primary" />
              </div>
            ) : byMonth.length === 0 ? (
              <div className="flex h-52 items-center justify-center text-sm text-text-muted">
                Sin datos en el período seleccionado
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={210}>
                <BarChart data={byMonth} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border,#e5e7eb)" />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} width={56} tickFormatter={fmtShort} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="total" name="Gastos" fill="#ef4444" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Pie chart by category */}
          <div className="lg:col-span-2 rounded-2xl border border-border bg-surface p-5 shadow-card">
            <h2 className="mb-4 text-base font-semibold text-text">Por categoría</h2>
            {isLoading ? (
              <div className="flex h-52 items-center justify-center">
                <Loader2 size={28} className="animate-spin text-primary" />
              </div>
            ) : byCategory.length === 0 ? (
              <div className="flex h-52 items-center justify-center text-sm text-text-muted">Sin datos</div>
            ) : (
              <ResponsiveContainer width="100%" height={210}>
                <PieChart>
                  <Pie
                    data={byCategory}
                    dataKey="total"
                    nameKey="category"
                    cx="50%" cy="50%"
                    innerRadius={50} outerRadius={80}
                    paddingAngle={2}
                    label={({ category, percent }) =>
                      `${(CATEGORY_LABELS[category] ?? category).slice(0, 10)} ${(percent * 100).toFixed(0)}%`
                    }
                    labelLine={false}
                  >
                    {byCategory.map((_, i) => (
                      <Cell key={i} fill={CATEGORY_COLORS[i % CATEGORY_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v: number) => fmt(v)} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* ── Comparativa ingresos vs gastos ── */}
        <div className="rounded-2xl border border-border bg-surface p-5 shadow-card">
          <h2 className="mb-4 text-base font-semibold text-text">Ingresos vs Gastos</h2>
          {isLoading ? (
            <div className="flex h-52 items-center justify-center">
              <Loader2 size={28} className="animate-spin text-primary" />
            </div>
          ) : comparison.length === 0 ? (
            <div className="flex h-52 items-center justify-center text-sm text-text-muted">Sin datos</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={comparison} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id="gIncome" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gExpense" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border,#e5e7eb)" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} width={60} tickFormatter={fmtShort} />
                <Tooltip content={<ChartTooltip />} />
                <Area type="monotone" dataKey="income"   name="Ingresos" stroke="#10b981" fill="url(#gIncome)"  strokeWidth={2} dot={false} />
                <Area type="monotone" dataKey="expenses" name="Gastos"   stroke="#ef4444" fill="url(#gExpense)" strokeWidth={2} dot={false} />
                <Legend />
              </AreaChart>
            </ResponsiveContainer>
          )}

          {/* Profit summary per month */}
          {!isLoading && comparison.length > 0 && (
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              {comparison.slice(-6).map(row => {
                const positive = row.profit >= 0;
                return (
                  <div key={row.month} className={`rounded-xl p-3 text-center ${positive ? 'bg-emerald-50 border border-emerald-100' : 'bg-red-50 border border-red-100'}`}>
                    <p className="text-xs text-text-muted font-medium">{row.label}</p>
                    <div className={`mt-1 flex items-center justify-center gap-1 text-sm font-bold ${positive ? 'text-emerald-600' : 'text-red-600'}`}>
                      {positive ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                      {fmtShort(Math.abs(row.profit))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Bottom row: Top suppliers + Category breakdown ── */}
        <div className="grid gap-6 lg:grid-cols-2">

          {/* Top suppliers */}
          <div className="rounded-2xl border border-border bg-surface p-5 shadow-card">
            <h2 className="mb-4 text-base font-semibold text-text">Top proveedores</h2>
            {isLoading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-8 animate-pulse rounded-lg bg-muted" />
                ))}
              </div>
            ) : topSuppliers.length === 0 ? (
              <p className="text-sm text-text-muted">Sin datos</p>
            ) : (
              <div className="space-y-3">
                {topSuppliers.map((s, i) => {
                  const pct = totals?.total_expenses
                    ? Math.round((s.total / totals.total_expenses) * 100)
                    : 0;
                  return (
                    <div key={s.supplier_name}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-medium text-text truncate max-w-[60%]">
                          {i + 1}. {s.supplier_name}
                        </span>
                        <span className="text-text-muted">{fmt(s.total)} <span className="text-xs">({pct}%)</span></span>
                      </div>
                      <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{ width: `${pct}%`, backgroundColor: CATEGORY_COLORS[i % CATEGORY_COLORS.length] }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Category breakdown table */}
          <div className="rounded-2xl border border-border bg-surface p-5 shadow-card">
            <h2 className="mb-4 text-base font-semibold text-text">Detalle por categoría</h2>
            {isLoading ? (
              <div className="space-y-2">
                {[...Array(6)].map((_, i) => <div key={i} className="h-8 animate-pulse rounded bg-muted" />)}
              </div>
            ) : byCategory.length === 0 ? (
              <p className="text-sm text-text-muted">Sin datos</p>
            ) : (
              <div className="divide-y divide-border">
                {byCategory.map((cat, i) => {
                  const pct = totals?.total_expenses
                    ? ((cat.total / totals.total_expenses) * 100).toFixed(1)
                    : '0';
                  return (
                    <div key={cat.category} className="flex items-center gap-3 py-2.5">
                      <div
                        className="h-3 w-3 shrink-0 rounded-full"
                        style={{ backgroundColor: CATEGORY_COLORS[i % CATEGORY_COLORS.length] }}
                      />
                      <span className="flex-1 text-sm text-text">
                        {CATEGORY_LABELS[cat.category] ?? cat.category}
                      </span>
                      <span className="text-xs text-text-muted">{cat.count} facturas</span>
                      <span className="text-sm font-semibold text-text w-28 text-right">{fmt(cat.total)}</span>
                      <span className="text-xs text-text-muted w-10 text-right">{pct}%</span>
                    </div>
                  );
                })}
                <div className="flex items-center justify-between pt-3 font-bold text-sm">
                  <span className="text-text">Total</span>
                  <span className="text-text">{fmt(totals?.total_expenses ?? 0)}</span>
                </div>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
